// =============================================================================
// FILE:
// frontend/src/modules/pms/material_logistics/components/DispatchGrid.js
// =============================================================================

import React from "react";

import {
  Eye,
  CheckCircle,
  Send
} from "lucide-react";

import {
  useNavigate
} from "react-router-dom";


// =============================================================================
// COMPONENT
// =============================================================================

export default function DispatchGrid({

  rows = [],

  loading = false

}) {

  // ===========================================================================
  // NAVIGATION
  // ===========================================================================
  const navigate = useNavigate();


  // ===========================================================================
  // LOADING
  // ===========================================================================
  if (loading) {

    return (

      <div style={styles.loader}>
        Loading dispatches...
      </div>
    );
  }


  // ===========================================================================
  // EMPTY
  // ===========================================================================
  if (!rows || rows.length === 0) {

    return (

      <div style={styles.emptyState}>
        No packing & dispatch records found
      </div>
    );
  }


  // ===========================================================================
  // MAIN RENDER
  // ===========================================================================
  return (

    <div style={styles.container}>


      {/* ===================================================================== */}
      {/* HEADER */}
      {/* ===================================================================== */}
      <div style={styles.headerRow}>


        <div style={styles.colPL}>
          Packing List
        </div>

        <div style={styles.colProject}>
          Project
        </div>

        <div style={styles.colStore}>
          Site Store
        </div>

        <div style={styles.colVehicle}>
          Vehicle
        </div>

        <div style={styles.colBoxes}>
          Boxes
        </div>

        <div style={styles.colWeight}>
          Weight
        </div>

        <div style={styles.colStatus}>
          Status
        </div>

        <div style={styles.colAction}>
          Actions
        </div>

      </div>


      {/* ===================================================================== */}
      {/* BODY */}
      {/* ===================================================================== */}
      <div style={styles.body}>


        {rows.map((row) => (

          <div
            key={row.id}
            style={styles.dataRow}
          >


            {/* --------------------------------------------------------------- */}
            {/* PACKING LIST */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colPL}>

              <div style={styles.primaryText}>
                {row.packing_list_no}
              </div>

              <div style={styles.secondaryText}>

                Dispatch:
                {" "}

                {row.dispatch_date || "-"}

              </div>

            </div>


            {/* --------------------------------------------------------------- */}
            {/* PROJECT */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colProject}>

              <div style={styles.primaryText}>
                {row.project_name || "-"}

              </div>

            </div>


            {/* --------------------------------------------------------------- */}
            {/* SITE STORE */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colStore}>

              <div style={styles.primaryText}>
                {row.site_store_name || "-"}

              </div>

            </div>


            {/* --------------------------------------------------------------- */}
            {/* VEHICLE */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colVehicle}>

              <div style={styles.primaryText}>
                {row.vehicle_no || "-"}

              </div>

              <div style={styles.secondaryText}>
                {row.driver_name || "-"}
              </div>

            </div>


            {/* --------------------------------------------------------------- */}
            {/* BOXES */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colBoxes}>

              {row.total_boxes || 0}

            </div>


            {/* --------------------------------------------------------------- */}
            {/* WEIGHT */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colWeight}>

              {row.total_weight || 0}

            </div>


            {/* --------------------------------------------------------------- */}
            {/* STATUS */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colStatus}>

              <span style={
                getStatusStyle(
                  row.dispatch_status
                )
              }>

                {row.dispatch_status}

              </span>

            </div>


            {/* --------------------------------------------------------------- */}
            {/* ACTIONS */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colAction}>


              {/* VIEW */}
              <button
                style={styles.actionBtn}
                onClick={() =>
                  navigate(
                    `/ipe/pms/packing-dispatch/${row.id}`
                  )
                }
                title="View Dispatch"
              >

                <Eye size={16} />

              </button>


              {/* SUBMIT */}
              {

                row.dispatch_status === "DRAFT" && (

                  <button
                    style={styles.actionBtn}
                    title="Submit Dispatch"
                  >

                    <Send size={16} />

                  </button>
                )
              }


              {/* APPROVE */}
              {

                row.dispatch_status === "SUBMITTED" && (

                  <button
                    style={styles.actionBtn}
                    title="Approve Dispatch"
                  >

                    <CheckCircle size={16} />

                  </button>
                )
              }

            </div>

          </div>

        ))}

      </div>

    </div>
  );
}


// =============================================================================
// STATUS STYLE
// =============================================================================

function getStatusStyle(status) {

  switch (status) {

    case "APPROVED":

      return styles.statusGreen;

    case "SUBMITTED":

      return styles.statusBlue;

    default:

      return styles.statusGray;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  container: {

    background: "#ffffff",

    borderRadius: 12,

    border: "1px solid #e5e7eb",

    overflow: "hidden"
  },

  headerRow: {

    display: "flex",

    padding: "14px 16px",

    background: "#f9fafb",

    borderBottom:
      "1px solid #e5e7eb",

    fontWeight: 600,

    fontSize: 13,

    color: "#374151"
  },

  body: {

    display: "flex",

    flexDirection: "column"
  },

  dataRow: {

    display: "flex",

    padding: "16px",

    borderBottom:
      "1px solid #f3f4f6",

    alignItems: "center"
  },

  colPL: {

    flex: 2
  },

  colProject: {

    flex: 2
  },

  colStore: {

    flex: 2
  },

  colVehicle: {

    flex: 2
  },

  colBoxes: {

    flex: 1,

    textAlign: "center"
  },

  colWeight: {

    flex: 1,

    textAlign: "center"
  },

  colStatus: {

    flex: 1.5,

    textAlign: "center"
  },

  colAction: {

    flex: 1.5,

    display: "flex",

    gap: 8,

    justifyContent: "center"
  },

  primaryText: {

    fontSize: 14,

    fontWeight: 600,

    color: "#111827"
  },

  secondaryText: {

    marginTop: 4,

    fontSize: 12,

    color: "#6b7280"
  },

  actionBtn: {

    width: 34,

    height: 34,

    borderRadius: 8,

    border:
      "1px solid #d1d5db",

    background: "#ffffff",

    cursor: "pointer",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    transition:
      "all 0.2s ease"
  },

  loader: {

    background: "#ffffff",

    borderRadius: 12,

    padding: 60,

    textAlign: "center",

    color: "#6b7280",

    fontSize: 14,

    boxShadow:
      "0 1px 3px rgba(0,0,0,0.08)"
  },

  emptyState: {

    background: "#ffffff",

    borderRadius: 12,

    padding: 60,

    textAlign: "center",

    color: "#9ca3af",

    fontSize: 14,

    boxShadow:
      "0 1px 3px rgba(0,0,0,0.08)"
  },

  statusGray: {

    background: "#f3f4f6",

    color: "#374151",

    padding: "6px 12px",

    borderRadius: 20,

    fontSize: 11,

    fontWeight: 600
  },

  statusBlue: {

    background: "#dbeafe",

    color: "#1d4ed8",

    padding: "6px 12px",

    borderRadius: 20,

    fontSize: 11,

    fontWeight: 600
  },

  statusGreen: {

    background: "#dcfce7",

    color: "#15803d",

    padding: "6px 12px",

    borderRadius: 20,

    fontSize: 11,

    fontWeight: 600
  }
};